import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Database, RefreshCw, ShieldAlert, Loader2, History, HelpCircle, AlertTriangle } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

// One state -> one badge. "unknown" and "building" are only ever shown when the backend itself reports
// them from actual evidence (a fresh/stale install lease, or a read error) — never guessed on the frontend.
function StateBadge({ state, detail }) {
  const map = {
    installed: ["bg-emerald-500/15 text-emerald-600 border-emerald-500/30", "Installed"],
    missing: ["bg-slate-500/15 text-slate-500 border-slate-500/30", "Missing"],
    building: ["bg-blue-500/15 text-blue-600 border-blue-500/30", "Building"],
    failed: ["bg-red-500/15 text-red-600 border-red-500/30", "Failed"],
    unknown: ["bg-amber-500/15 text-amber-600 border-amber-500/30", "Unknown"],
  };
  const [cls, label] = map[state] || map.unknown;
  return (
    <span className="inline-flex items-center gap-1" title={detail || ""}>
      <Badge className={`${cls} text-[10px]`} data-testid={`idx-state-${state}`}>{label}</Badge>
      {state === "building" && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
      {detail && <HelpCircle className="w-3 h-3 text-muted-foreground" />}
    </span>
  );
}

function DefinitionsCard({ env, definitions }) {
  return (
    <Card className="p-4 space-y-3" data-testid="idx-definitions-card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Database className="w-4 h-4" /> Approved Index Definitions</h3>
        <Badge className={env === "production" ? "bg-red-600 text-white border-red-600" : env === "unknown" ? "bg-amber-500/15 text-amber-600 border-amber-500/30" : ""}
               variant={env === "production" || env === "unknown" ? undefined : "outline"} data-testid="idx-env-badge">
          {env === "production" ? "PRODUCTION" : env === "unknown" ? "Unverified Environment" : "Preview"}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        Only the definitions registered in application code below can ever be installed from this screen — nothing
        arbitrary can be supplied from the browser.
      </p>
      <div className="space-y-2">
        {definitions.map((d) => (
          <div key={d.name} className="flex items-center justify-between border rounded-md p-2 text-xs" data-testid={`idx-def-${d.name}`}>
            <div>
              <div className="font-mono font-medium">{d.name}</div>
              <div className="text-muted-foreground">{d.collection}: {d.keys.map((k) => `${k[0]}:${k[1]}`).join(", ")}</div>
            </div>
            <Badge variant="outline" className="text-[10px]">{d.profile_label}</Badge>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function IndexMaintenancePanel() {
  const [env, setEnv] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");
  const [rows, setRows] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [selectedTenants, setSelectedTenants] = useState(new Set());
  const [selectedIndexes, setSelectedIndexes] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [installBusy, setInstallBusy] = useState(false);
  const [installResults, setInstallResults] = useState(null);
  const [auditRows, setAuditRows] = useState(null);
  const [stepUp, setStepUp] = useState({ password: "", code: "" });
  const [installErr, setInstallErr] = useState("");

  const loadDefinitions = async () => {
    try {
      const { data } = await api.get("/superadmin/maintenance/index-health/definitions");
      setEnv(data.environment);
      setDefinitions(data.definitions);
      setSelectedIndexes(new Set(data.definitions.map((d) => d.name)));
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadAudit = async () => {
    try {
      const { data } = await api.get("/superadmin/maintenance/index-health/audit", { params: { limit: 50 } });
      setAuditRows(data.audit);
    } catch (e) { toast.error(apiError(e)); }
  };

  const checkStatus = async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get("/superadmin/maintenance/index-health/status", {
        params: tenantFilter.trim() ? { tenant_id: tenantFilter.trim() } : {},
      });
      setEnv(data.environment);
      setRows(data.tenants);
      setSelectedTenants(new Set());
    } catch (e) { toast.error(apiError(e)); setRows([]); }
    setStatusLoading(false);
  };

  useEffect(() => { loadDefinitions(); loadAudit(); }, []);

  const toggleTenant = (bid) => setSelectedTenants((prev) => {
    const next = new Set(prev);
    next.has(bid) ? next.delete(bid) : next.add(bid);
    return next;
  });
  const toggleIndex = (name) => setSelectedIndexes((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const runInstall = async () => {
    setInstallErr("");
    if (env === "production" && !stepUp.password) { setInstallErr("Your Super Admin password is required to install on Production."); return; }
    setInstallBusy(true);
    try {
      const { data } = await api.post("/superadmin/maintenance/index-health/install", {
        tenant_ids: [...selectedTenants], index_names: [...selectedIndexes],
        password: stepUp.password || undefined, code: stepUp.code || undefined,
      });
      setConfirmOpen(false);
      setInstallResults(data.results);
      toast.success(`Install run finished for ${data.results.length} tenant(s)`);
      await checkStatus();
      await loadAudit();
      setStepUp({ password: "", code: "" });
    } catch (e) { setInstallErr(apiError(e)); }
    setInstallBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="index-maintenance-panel">
      {definitions.length > 0 && <DefinitionsCard env={env} definitions={definitions} />}

      <Card className="p-4 space-y-3" data-testid="idx-status-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-sm">Tenant Index Status</h3>
          <div className="flex items-center gap-2">
            <Input placeholder="Specific tenant_id (optional)" value={tenantFilter}
                   onChange={(e) => setTenantFilter(e.target.value)} className="h-8 w-56 text-xs"
                   data-testid="idx-tenant-filter-input" />
            <Button size="sm" variant="outline" onClick={checkStatus} disabled={statusLoading} data-testid="idx-check-status-btn">
              {statusLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Check Index Status
            </Button>
          </div>
        </div>

        {rows === null && <p className="text-xs text-muted-foreground">Click "Check Index Status" to load tenant results (read-only).</p>}
        {rows !== null && rows.length === 0 && <p className="text-xs text-muted-foreground">No tenants found.</p>}

        {rows && rows.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Tenant</TableHead>
                {definitions.map((d) => <TableHead key={d.name}>{d.name}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.tenant_id} data-testid={`idx-row-${row.tenant_id}`}>
                  <TableCell>
                    <Checkbox checked={selectedTenants.has(row.tenant_id)} onCheckedChange={() => toggleTenant(row.tenant_id)}
                              data-testid={`idx-select-tenant-${row.tenant_id}`} />
                  </TableCell>
                  <TableCell className="text-xs">
                    {row.business_name
                      ? <><span className="font-medium">{row.business_name}</span><span className="text-muted-foreground font-mono"> ({row.tenant_id})</span></>
                      : <span className="font-mono">{row.tenant_id}</span>}
                  </TableCell>
                  {definitions.map((d) => (
                    <TableCell key={d.name}>
                      <StateBadge state={row.indexes[d.name]?.state} detail={row.indexes[d.name]?.detail} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-3">
            {definitions.map((d) => (
              <label key={d.name} className="flex items-center gap-1.5 text-xs" data-testid={`idx-select-index-${d.name}`}>
                <Checkbox checked={selectedIndexes.has(d.name)} onCheckedChange={() => toggleIndex(d.name)} />
                {d.name}
              </label>
            ))}
          </div>
          <div className="flex flex-col items-end gap-1">
            {env === "unknown" && (
              <p className="text-[11px] text-amber-600" data-testid="idx-unknown-env-hint">
                Installation is refused until the environment can be verified as Preview or Production.
              </p>
            )}
            <Button size="sm" disabled={!selectedTenants.size || !selectedIndexes.size || installBusy || env === "unknown"}
                    onClick={() => { setInstallErr(""); setStepUp({ password: "", code: "" }); setConfirmOpen(true); }} data-testid="idx-install-btn">
              <ShieldAlert className="w-3.5 h-3.5 mr-1.5" /> Install Missing Approved Indexes
            </Button>
          </div>
        </div>

        {installResults && (
          <div className="space-y-1 pt-2 border-t" data-testid="idx-install-results">
            <p className="text-xs font-medium">Last install run:</p>
            {installResults.map((r) => (
              <div key={r.tenant_id} className="text-xs font-mono flex items-center gap-2 flex-wrap">
                {r.tenant_id}: {Object.entries(r.results).map(([name, outcome]) => (
                  <Badge key={name} variant="outline" className="text-[10px]">{name}: {outcome.status}{outcome.error ? ` (${outcome.error})` : ""}</Badge>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-3" data-testid="idx-audit-card">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm flex items-center gap-2"><History className="w-4 h-4" /> Audit History</h3>
          <Button size="sm" variant="outline" onClick={loadAudit} data-testid="idx-refresh-audit-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead><TableHead>Actor</TableHead><TableHead>Operation</TableHead>
              <TableHead>Target</TableHead><TableHead>Env</TableHead><TableHead>Result</TableHead><TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(auditRows || []).map((a) => (
              <TableRow key={a.id} data-testid={`idx-audit-row-${a.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{fmt(a.at)}</TableCell>
                <TableCell className="text-xs">{a.actor?.name || a.actor?.email || a.actor?.id}</TableCell>
                <TableCell className="text-xs">{a.operation}</TableCell>
                <TableCell className="text-xs font-mono">
                  {a.target?.tenant_id || (a.target?.tenant_ids || []).join(", ")}
                  {a.target?.index_name ? ` / ${a.target.index_name}` : ""}
                  {a.target?.index_names ? ` / ${a.target.index_names.join(", ")}` : ""}
                </TableCell>
                <TableCell className="text-xs">
                  {a.target?.environment
                    ? <Badge variant="outline" className={`text-[10px] ${a.target.environment === "production" ? "border-red-500/40 text-red-600" : ""}`}>{a.target.environment}</Badge>
                    : "-"}
                </TableCell>
                <TableCell className="text-xs">
                  <Badge className={a.result === "success" ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-[10px]" : "bg-red-500/15 text-red-600 border-red-500/30 text-[10px]"}>
                    {a.result}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{a.details?.error || a.details?.status || "-"}</TableCell>
              </TableRow>
            ))}
            {auditRows && auditRows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-xs text-muted-foreground text-center">No maintenance activity yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setInstallErr(""); }}>
        <DialogContent data-testid="idx-install-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{env === "production" ? "Install approved indexes on PRODUCTION?" : "Install approved indexes?"}</DialogTitle>
            <DialogDescription>
              This will attempt <strong>{[...selectedIndexes].join(", ")}</strong> on <strong>{selectedTenants.size} tenant(s)</strong>:
              {" "}{[...selectedTenants].join(", ")}.
              <br /><br />
              Already-installed indexes are left untouched (idempotent, no duplicates). Index builds consume
              database resources — recommended to run outside active store hours for larger tenants.
            </DialogDescription>
          </DialogHeader>
          {env === "production" && (
            <div className="space-y-3 border border-red-500/30 bg-red-500/5 rounded-md p-3">
              <p className="text-xs flex items-center gap-1.5 text-red-600 font-medium">
                <AlertTriangle className="w-3.5 h-3.5" /> This targets Production. Re-enter your credentials to authorize.
              </p>
              <div><Label className="text-xs">Your Super Admin password</Label>
                <Input type="password" value={stepUp.password} onChange={(e) => setStepUp({ ...stepUp, password: e.target.value })} data-testid="idx-stepup-password" />
              </div>
              <div><Label className="text-xs">Authenticator code (if 2FA enabled)</Label>
                <Input value={stepUp.code} onChange={(e) => setStepUp({ ...stepUp, code: e.target.value.trim() })} className="font-mono" data-testid="idx-stepup-code" />
              </div>
            </div>
          )}
          {installErr && <p className="text-sm text-rose-500" data-testid="idx-install-confirm-error">{installErr}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="idx-install-confirm-cancel">Cancel</Button>
            <Button onClick={runInstall} disabled={installBusy} data-testid="idx-install-confirm-ok">
              {installBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Confirm Install
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
